import { Suspense, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../Navbar/Navbar';
import AnnouncementBar from '../AnnouncementBar/AnnouncementBar';
import Footer from '../Footer/Footer';
import BottomTabBar from '../BottomTabBar/BottomTabBar';
import StickyMobileBar from '../StickyMobileBar/StickyMobileBar';
import ScrollRevealManager from './ScrollRevealManager';
import ExitIntentPopup from '../ExitIntentPopup/ExitIntentPopup';
import CartDrawer from '../../cart/CartDrawer/CartDrawer';
import PageLoaderFallback from './PageLoaderFallback';
import { useCart } from '../../../context/CartContext';
import './Layout.css';

/**
 * Routes where we strip the UI to bare minimum.
 * No distractions = higher checkout completion rate.
 * Baymard Institute: every extra element on checkout costs ~1-3% conversion.
 */
const CHECKOUT_ROUTES = ['/checkout', '/payment', '/order-confirmation', '/order-success'];

/**
 * Routes where footer is never shown (cart, account orders).
 * Footer contains exit links; on cart/order pages we keep focus on primary action.
 * See docs/FOOTER_BEHAVIOUR.md.
 */
const NO_FOOTER_ROUTES = ['/cart', '/account/orders'];

/**
 * Routes where bottom tab bar should be hidden.
 * Cart page has its own CTAs — tab bar competes with them.
 */
const NO_BOTTOM_TAB_ROUTES = [...CHECKOUT_ROUTES, '/cart'];

/**
 * Exit intent should NEVER fire on checkout or cart.
 * It's a last-resort retention tool, not a checkout disruptor.
 */
const NO_EXIT_INTENT_ROUTES = [...CHECKOUT_ROUTES, '/cart'];

function Layout({
  children,
  showFooter = true,
  navbarVariant = 'default',
}) {
  const { isCartOpen, closeCart } = useCart();
  const { pathname } = useLocation();

  const isCheckoutFlow = CHECKOUT_ROUTES.some((r) => pathname.startsWith(r));
  const isNoFooterRoute = NO_FOOTER_ROUTES.some((r) => pathname.startsWith(r));
  const showBottomTab = !NO_BOTTOM_TAB_ROUTES.some((r) => pathname.startsWith(r));
  const showExitIntent = !NO_EXIT_INTENT_ROUTES.some((r) => pathname.startsWith(r));

  const showFooterFinal = showFooter && !isCheckoutFlow && !isNoFooterRoute;

  /**
   * Scroll to top on route change — React Router doesn't do this by default.
   * This is critical for UX: users shouldn't land mid-page after navigation.
   */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  /**
   * Set CSS variables on body for StickyMobileBar and Toast positioning.
   * When bottom tab bar is hidden (checkout, cart), sticky bar sits at bottom: 0.
   * Sticky bar height is 64px (min-height from StickyMobileBar CSS).
   */
  const isProductPage = pathname.startsWith('/product');
  
  useEffect(() => {
    document.body.style.setProperty(
      '--sticky-bar-bottom',
      showBottomTab ? 'var(--tab-bar-height, 60px)' : '0px'
    );
    document.body.style.setProperty(
      '--sticky-bar-height',
      isProductPage ? '64px' : '0px'
    );
    return () => {
      document.body.style.removeProperty('--sticky-bar-bottom');
      document.body.style.removeProperty('--sticky-bar-height');
    };
  }, [showBottomTab, isProductPage]);

  /**
   * StickyMobileBar and BottomTabBar CANNOT both be visible.
   * StickyMobileBar = product page "Add to Cart" bar (contextual, per-page).
   * BottomTabBar = global navigation.
   * On product pages, StickyMobileBar takes over the bottom.
   * The CSS handles the visual swap — see Layout.css.
   */

  return (
    <>
      {/* Accessibility: skip nav for keyboard users and screen readers */}
      <a href="#main-content" className="layout__skip-link">
        Skip to main content
      </a>

      <AnnouncementBar />
      <Navbar variant={isCheckoutFlow ? 'minimal' : navbarVariant} />
      <ScrollRevealManager />

      {/*
        CartDrawer uses createPortal internally (it should — fix it if it doesn't).
        Placing it here keeps it in logical DOM order while portal handles rendering.
      */}
      <CartDrawer isOpen={isCartOpen} onClose={closeCart} />

      {/*
        padding-bottom keeps content above the fixed bottom tab bar.
        The CSS variable --bottom-bar-height is set based on showBottomTab.
        Without this, your fixed BottomTabBar eats your footer and CTAs.
      */}
      <main
        id="main-content"
        className="layout__main"
        style={{ '--bottom-bar-height': showBottomTab ? 'var(--tab-bar-height)' : '0px' }}
        tabIndex={-1}
        role="main"
      >
        {/*
          Suspense boundary: prevents white flash on lazy-loaded route components.
          Replace the fallback with your skeleton/spinner component.
        */}
        <Suspense fallback={<PageLoaderFallback label="Loading page" />}>
          {children}
        </Suspense>
      </main>

      {/* Footer: never on checkout or no-footer routes (cart, account/orders). See docs/FOOTER_BEHAVIOUR.md */}
      {showFooterFinal && <Footer />}

      {/*
        BottomTabBar: global nav. Hidden on checkout + cart.
        StickyMobileBar: contextual (e.g. Add to Cart on PDP).
        They share the same bottom slot — StickyMobileBar overlays when active.
        See Layout.css for the stacking logic.
      */}
      {showBottomTab && <BottomTabBar />}
      <StickyMobileBar />

      {/* ExitIntentPopup: only on browsing pages, never during purchase flow */}
      {showExitIntent && <ExitIntentPopup />}
    </>
  );
}

export default Layout;
